import { useMemo } from 'react';

import { useAppConfig } from '@common/app-config';
import { formatCategoryList, useInitLikedProducts, useLikeProductService } from '@widgets/like-product';
import { UxGoodsCardItemForVerticalProductCard as UxGoodsCardItem } from '@common/product-card';
import {
  parseQueryString,
  PDPGoodsGroup,
  PDPGoodsGroup_goods_carousel_component_list as ProductCardItem,
  usePdpBaseInfo,
  usePdpRelatedProductRecommendGroups,
} from '@domains/pdp-common';

import { listInfoConfig } from '../components/carousel/RelatedRecommendCarousels';
import { useBottomRecommendListLog } from '../log/useBottomRecommendListLog';

const beautyProductGroup = 'beauty_product_item_list';

const convertList = (list: ProductCardItem[]) => list.map(({ catalog_product_id }) => catalog_product_id || '');

export const useBottomRelatedRecommendListService = (productId: string, isInfiniteRecommend?: boolean) => {
  const { isZigzagApp } = useAppConfig();
  const {
    flags: { is_selected_shop },
  } = usePdpBaseInfo({ catalog_product_id: productId });
  const { bottom_area } = usePdpRelatedProductRecommendGroups({
    catalog_product_id: productId,
    isSelectedShop: Boolean(is_selected_shop && isZigzagApp),
  });
  const { impressionProductItem, clickProductItem, savedProduct, moreView, clickSelectedMoreViewUBL } =
    useBottomRecommendListLog(productId);
  const { onLikeProduct } = useLikeProductService();

  const topDisplayGroupList = isInfiniteRecommend ? ['related_item_list'] : ['related_item_list', 'similar_item_list'];

  const { topRelatedRecommendList, bottomRelatedRecommendList } = bottom_area.reduce(
    (prev, curr) => {
      if (topDisplayGroupList.includes(curr.group_type)) {
        prev.topRelatedRecommendList.push(curr);
        return prev;
      }

      if (isInfiniteRecommend) return prev;

      prev.bottomRelatedRecommendList.push(curr);
      return prev;
    },
    { topRelatedRecommendList: [] as PDPGoodsGroup[], bottomRelatedRecommendList: [] as PDPGoodsGroup[] },
  );

  const hasRecommendList = !!bottom_area[0]?.goods_carousel.component_list.length;

  const memoizedBeautyProductItemList = useMemo(
    () => bottom_area.filter(({ group_type }) => beautyProductGroup.includes(group_type)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasRecommendList],
  );

  const updateBottomList = () => {
    const beautyProductIndex = bottomRelatedRecommendList.findIndex(({ group_type }) =>
      beautyProductGroup.includes(group_type),
    );

    if (memoizedBeautyProductItemList.length) {
      bottomRelatedRecommendList.splice(beautyProductIndex, 1, memoizedBeautyProductItemList[0]);
    }
  };

  updateBottomList();

  const itemList = useMemo(
    () => bottom_area.flatMap((item) => item.goods_carousel.component_list) || [],
    [bottom_area],
  );

  useInitLikedProducts({
    data: itemList,
    convert: convertList,
  });

  const onImpressionProduct = (listInfo: listInfoConfig) => (item: UxGoodsCardItem) =>
    impressionProductItem({ ...item, ...listInfo });

  const onClickProduct = (listInfo: listInfoConfig) => (item: UxGoodsCardItem) =>
    clickProductItem({ ...item, ...listInfo });

  const onSavedProduct = (listInfo: listInfoConfig) => (checked: boolean, item: UxGoodsCardItem) => {
    const inAppLikeProductLog = {
      object_idx: listInfo.position,
      object_section: listInfo.groupType,
      main_title: listInfo.mainTitle,
      ...{
        original_product_id: productId,
        ...(item.log ? parseQueryString(item.log) : undefined),
      },
    };

    const inAppLikeProductPayload = {
      like: checked,
      browsingType: item.browsing_type,
      shopId: Number(item.shop_id),
      shopProductNo: item.shop_product_no,
      catalogProductId: item.catalog_product_id,
      title: item.title || '',
      goodsUrl: item.product_url || '',
      imageUrl: item.image_url || '',
      price: Number(item.final_price),
      discountRate: Number(item.discount_rate),
      isZpay: Boolean(item.zpay),
      isFreeShipping: Boolean(item.free_shipping),
      log: inAppLikeProductLog,
    };

    const airbridge = {
      brandID: item.shop_id,
      brandName: item.shop_name,
      productID: item.catalog_product_id,
      productName: item.title,
      price: item.final_price,
      categoryID: formatCategoryList(item.managed_category_list, 'id'),
      categoryName: formatCategoryList(item.managed_category_list, 'value'),
    };

    const webLikeProductPayload = {
      checked,
      productData: { catalog_product_id: item.catalog_product_id || '', shop_id: item.shop_id || '' },
      airbridge,
      sendLog: (checked: boolean) =>
        savedProduct({
          category: checked ? 'save_product' : 'remove_saved_product',
          position: listInfo.position,
          groupType: listInfo.groupType,
          item,
        }),
    };

    onLikeProduct({ inAppLikeProductPayload, webLikeProductPayload });
  };

  const onClickMore = () => {
    if (is_selected_shop) {
      clickSelectedMoreViewUBL();
    }
  };
  return {
    isHide: !hasRecommendList,
    topRelatedRecommendList,
    bottomRelatedRecommendList,
    onImpressionProduct,
    onClickProduct,
    onSavedProduct,
    moreView,
    onClickMore,
  };
};
